import { DateTime, Duration } from 'luxon';
import {
  AssignableUnit,
  DateTimeInterval,
  NotInServiceScheduleEntry,
  OrderScheduleEntry,
  ProductCalendar,
  ProductQuantityAvailability,
  ProductSchedule,
  ProductUtilization,
  QuantityAvailability,
  ScheduleEntry,
  SoldScheduleEntry,
  UnitCalendar,
  UnitSchedule,
  UnitUtilization,
  Utilization,
  WorkOrderScheduleEntry,
} from './availability';
import { ProductParsers } from '../product/product-parsers';
import { OrderReturnStatus, OrderStatus } from '../order/order';
import { WorkOrderStatus } from '../work-order/work-order';

export namespace AvailabilityParsers {
  import ProductJson = ProductParsers.ProductJson;
  import toProduct = ProductParsers.toProduct;
  import ProductInventoryUnitJson = ProductParsers.ProductInventoryUnitJson;
  import toProductInventoryUnit = ProductParsers.toProductInventoryUnit;

  export class ProductSchedulesJson {
    readonly product!: ProductParsers.ProductJson;
    readonly unitSchedules!: UnitScheduleJson[];
    readonly unassignedOrders!: OrderScheduleEntryJson[];
    readonly utilizationThresholdHigh!: string | undefined;
    readonly utilizationThresholdLow!: string | undefined;
  }

  export class NotInServiceScheduleEntryJson {
    readonly type!: 'NotInService';
    readonly from!: string;
    readonly to!: string;
  }

  export class OrderScheduleEntryJson {
    readonly type!: 'Order';
    readonly locationId!: number;
    readonly location!: string;
    readonly orderNumber!: string;
    readonly orderId!: number;
    readonly orderProductId!: number;
    readonly orderStatus!: OrderStatus;
    readonly orderReturnStatus!: OrderReturnStatus;
    readonly renterName!: string;
    readonly from!: string;
    readonly to?: string;
    readonly bufferBefore!: string;
    readonly bufferAfter!: string;
  }

  export class SoldScheduleEntryJson {
    readonly type!: 'Sold';
    readonly from!: string;
    readonly to?: string;
  }

  export class WorkOrderScheduleEntryJson {
    readonly type!: 'WorkOrder';
    readonly workOrderId!: string;
    readonly workOrderStatus!: WorkOrderStatus;
    readonly from!: string;
    readonly to?: string;
  }

  export type ScheduleEntryJson =
    | NotInServiceScheduleEntryJson
    | OrderScheduleEntryJson
    | SoldScheduleEntryJson
    | WorkOrderScheduleEntryJson;

  export class UnitScheduleJson {
    readonly unit!: ProductParsers.ProductInventoryUnitJson;
    readonly schedule!: ScheduleEntryJson[];
  }

  function toOrderScheduleEntry(input: OrderScheduleEntryJson): OrderScheduleEntry {
    return {
      type: 'Order',
      locationId: input.locationId,
      location: input.location,
      orderNumber: input.orderNumber,
      orderId: input.orderId,
      orderProductId: input.orderProductId,
      orderStatus: input.orderStatus,
      orderReturnStatus: input.orderReturnStatus,
      renterName: input.renterName,
      from: DateTime.fromISO(input.from, { setZone: true }),
      to: input.to ? DateTime.fromISO(input.to, { setZone: true }) : undefined,
      bufferBefore: Duration.fromISO(input.bufferBefore),
      bufferAfter: Duration.fromISO(input.bufferAfter),
    };
  }

  function toNotInServiceScheduleEntry(input: NotInServiceScheduleEntryJson): NotInServiceScheduleEntry {
    return {
      type: 'NotInService',
      from: DateTime.fromISO(input.from, { setZone: true }),
      to: DateTime.fromISO(input.to, { setZone: true }),
    };
  }

  function toSoldScheduleEntry(input: SoldScheduleEntryJson): SoldScheduleEntry {
    return {
      type: 'Sold',
      from: DateTime.fromISO(input.from, { setZone: true }),
      to: input.to ? DateTime.fromISO(input.to, { setZone: true }) : undefined,
    };
  }

  function toWorkOrderScheduleEntry(input: WorkOrderScheduleEntryJson): WorkOrderScheduleEntry {
    return {
      type: 'WorkOrder',
      workOrderId: input.workOrderId,
      workOrderStatus: input.workOrderStatus,
      from: DateTime.fromISO(input.from, { setZone: true }),
      to: input.to ? DateTime.fromISO(input.to, { setZone: true }) : undefined,
    };
  }

  export function toScheduleEntry(input: ScheduleEntryJson): ScheduleEntry {
    switch (input.type) {
      case 'NotInService':
        return toNotInServiceScheduleEntry(input);
      case 'Order':
        return toOrderScheduleEntry(input);
      case 'Sold':
        return toSoldScheduleEntry(input);
      case 'WorkOrder':
        return toWorkOrderScheduleEntry(input);
    }
  }

  export function toUnitSchedules(input: UnitScheduleJson): UnitSchedule {
    return {
      unit: ProductParsers.toProductInventoryUnit(input.unit),
      schedule: input.schedule.map((s) => toScheduleEntry(s)),
    };
  }

  export function toProductSchedule(input: ProductSchedulesJson): ProductSchedule {
    return {
      product: ProductParsers.toProduct(input.product),
      unitSchedules: input.unitSchedules.map(toUnitSchedules),
      unassignedOrders: input.unassignedOrders.map(toOrderScheduleEntry),
      utilizationThresholdLow: input.utilizationThresholdLow ? parseFloat(input.utilizationThresholdLow) : 0,
      utilizationThresholdHigh: input.utilizationThresholdHigh ? parseFloat(input.utilizationThresholdHigh) : 0,
    };
  }

  export class DateTimeIntervalJson {
    start?: string;
    end?: string;
  }

  export function toDateTimeInterval(input: DateTimeIntervalJson): DateTimeInterval {
    return new DateTimeInterval(
      input.start ? DateTime.fromISO(input.start, { setZone: true }) : undefined,
      input.end ? DateTime.fromISO(input.end, { setZone: true }) : undefined,
    );
  }

  export class QuantityAvailabilityJson {
    quantity!: number;
    stock?: number;
    dateTimeInterval!: DateTimeIntervalJson;
  }

  export function toQuantityAvailability(input: QuantityAvailabilityJson): QuantityAvailability {
    return {
      quantity: input.quantity,
      stock: input.stock,
      dateTimeInterval: toDateTimeInterval(input.dateTimeInterval),
    };
  }

  export class ProductQuantityAvailabilityJson {
    product!: ProductJson;
    availability!: QuantityAvailabilityJson[];
  }

  export function toProductQuantityAvailability(input: ProductQuantityAvailabilityJson): ProductQuantityAvailability {
    return {
      product: toProduct(input.product),
      availability: input.availability.map(toQuantityAvailability),
    };
  }

  export class ProductUtilizationJson {
    readonly product!: ProductJson;
    readonly utilization!: number;
    readonly algorithm!: string;
    readonly utilizationThresholdHigh!: number;
    readonly utilizationThresholdLow!: number;
    readonly unitUtilization!: UnitUtilizationJson[];
  }

  export function toProductUtilization(input: ProductUtilizationJson): ProductUtilization {
    return {
      product: toProduct(input.product),
      algorithm: input.algorithm,
      utilization: input.utilization,
      utilizationThresholdLow: input.utilizationThresholdLow,
      utilizationThresholdHigh: input.utilizationThresholdHigh,
      unitUtilization: input.unitUtilization.map(toUnitUtilization),
    };
  }

  export class UnitUtilizationJson {
    readonly unit!: ProductInventoryUnitJson;
    readonly algorithm!: string;
    readonly utilization!: number;
    readonly utilizationThresholdHigh!: number;
    readonly utilizationThresholdLow!: number;
  }

  export function toUnitUtilization(input: UnitUtilizationJson): UnitUtilization {
    return {
      unit: toProductInventoryUnit(input.unit),
      algorithm: input.algorithm,
      utilization: input.utilization,
      utilizationThresholdLow: input.utilizationThresholdLow,
      utilizationThresholdHigh: input.utilizationThresholdHigh,
    };
  }

  export class AssignableUnitJson {
    unit!: ProductInventoryUnitJson;
    status!: 'Available' | 'Scheduled' | 'On Rent' | 'Hard Down';
  }

  export function toAssignableUnit(input: AssignableUnitJson): AssignableUnit {
    return {
      unit: toProductInventoryUnit(input.unit),
      status: input.status,
    };
  }

  export class ProductCalendarJson {
    readonly product!: ProductParsers.ProductJson;
    readonly utilization?: Utilization;
    readonly quantityAvailability!: QuantityAvailabilityJson[];
    readonly unassignedOrders!: OrderScheduleEntryJson[];
    readonly unitCalendar!: UnitCalendarJson[];
  }

  export class UnitCalendarJson {
    readonly unit!: ProductParsers.ProductInventoryUnitJson;
    readonly utilization?: Utilization;
    readonly schedule!: ScheduleEntryJson[];
  }

  export function toUnitCalendar(input: UnitCalendarJson): UnitCalendar {
    return {
      unit: ProductParsers.toProductInventoryUnit(input.unit),
      utilization: input.utilization,
      schedule: input.schedule.map((s) => toScheduleEntry(s)),
    };
  }

  export function toProductCalendar(input: ProductCalendarJson): ProductCalendar {
    return {
      product: ProductParsers.toProduct(input.product),
      unitCalendar: input.unitCalendar.map(toUnitCalendar),
      utilization: input.utilization,
      unassignedOrders: input.unassignedOrders.map(toOrderScheduleEntry),
      quantityAvailability: input.quantityAvailability.map(toQuantityAvailability),
    };
  }
}
